import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../i18n';
import { L } from '../tokens';
import { useViewportKind } from '../useViewport';
import { countText, moneyText, pctText, supplierName } from './reportFormat';

// Leo's "By company" table of Commissions and Forecast: company (a link to its page), share bar with its percentage, rate, net and commission, and a total row. The five biggest
// companies are shown, the rest as one "Others" row, with "Show all N companies" to open the whole list (each company keeps its own statement button there). A company without a rate
// (or with two different rates) shows "—" for rate and commission and is left out of the total (G10). On a phone each company is a small card.
//   rows      [{ supplier: { id, name, unknown }, share, rate, rate_conflict, net, commission }]
//   action    optional { label, hint, busyId, onClick(row) }: a button in each company's row (the statement download)

const TOP = 5;

function RateCell({ row, t }) {
  if (row.rate_conflict) return <span title={t('reports.rateConflict')}>{t('reports.none')}</span>;
  return <span>{row.rate == null ? t('reports.none') : `${row.rate}%`}</span>;
}

function Share({ share, color }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <span aria-hidden="true" style={{ width: 70, height: 8, borderRadius: 4, background: L.rowLine, position: 'relative', flexShrink: 0 }}>
        <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${share == null ? 0 : Math.min(100, share)}%`, borderRadius: 4, background: color }} />
      </span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{pctText(share)}</span>
    </span>
  );
}

// the companies after the fifth, as one row: their net and commission added up (a company without a rate adds no commission)
function othersOf(rest) {
  const rated = rest.filter((r) => r.commission != null);
  const sum = (xs, k) => Math.round(xs.reduce((s, r) => s + (r[k] || 0), 0) * 100) / 100;
  const share = rated.length && rest.some((r) => r.share != null) ? Math.round(rest.reduce((s, r) => s + (r.share || 0), 0) * 10) / 10 : null;
  return { others: true, supplier: null, rate: null, rate_conflict: false, net: sum(rest, 'net'), commission: rated.length ? sum(rated, 'commission') : null, share, count: rest.length };
}

export default function CompanyTable({ rows, total, currency, netLabel, commissionLabel, barColor = L.accent, action = null, testid = 'company-table' }) {
  const t = useT();
  const phone = useViewportKind() === 'phone';
  const [all, setAll] = useState(false);
  const shown = all || rows.length <= TOP + 1 ? rows : [...rows.slice(0, TOP), othersOf(rows.slice(TOP))];
  const collapsible = rows.length > TOP + 1;
  const label = (r) => (r.others ? t('reports.others', { n: countText(r.count) }) : supplierName(r.supplier, t));
  const nameCell = (r) => {
    const text = label(r);
    if (r.others || !r.supplier || r.supplier.unknown) return <span>{text}</span>;
    return <Link to={`/reports/suppliers/${r.supplier.id}`} data-testid={`company-link-${r.supplier.id}`} style={{ color: L.accent, textDecoration: 'none', fontWeight: 600 }}>{text}</Link>;
  };
  const rowKey = (r) => (r.others ? 'others' : r.supplier.id);
  const cm = (r) => (r.commission == null ? t('reports.none') : moneyText(r.commission, currency));
  const btn = (r) => action && !r.others && (
    <button type="button" onClick={() => action.onClick(r)} disabled={action.busyId === r.supplier.id} title={action.hint} data-testid={`statement-${r.supplier.id}`}
      style={{ height: 34, padding: '0 12px', borderRadius: 17, border: `1px solid ${L.border}`, background: '#FFFFFF', color: L.text, font: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', opacity: action.busyId === r.supplier.id ? 0.5 : 1 }}>
      {action.label}
    </button>
  );
  const toggle = collapsible && (
    <button type="button" onClick={() => setAll((v) => !v)} data-testid="company-toggle"
      style={{ alignSelf: 'flex-start', margin: '10px 0 0', padding: 0, border: 'none', background: 'none', color: L.accent, font: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
      {all ? t('reports.showFewer') : t('reports.showAll', { n: countText(rows.length) })}
    </button>
  );
  if (!rows.length) return <div data-testid={testid} style={{ color: L.muted, padding: '8px 0' }}>{t('reports.empty')}</div>;
  if (phone) {
    return (
      <div data-testid={testid} style={{ display: 'flex', flexDirection: 'column' }}>
        {shown.map((r) => (
          <div key={rowKey(r)} data-testid={r.others ? 'company-others' : `company-${r.supplier.id}`} style={{ borderTop: `1px solid ${L.rowLine}`, padding: '12px 56px 12px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontSize: 16, fontWeight: 600, overflowWrap: 'anywhere' }}>{nameCell(r)}</span>
              <span style={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{cm(r)}</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: 13, color: L.muted }}>
              <span>{t('reports.col.share')}: {pctText(r.share)}</span>
              <span>{t('reports.col.rate')}: <RateCell row={r} t={t} /></span>
              <span>{netLabel}: {moneyText(r.net, currency)}</span>
            </div>
            {btn(r)}
          </div>
        ))}
        {toggle}
        <div data-testid="company-total" style={{ borderTop: `2px solid ${L.border}`, padding: '12px 56px 12px 0', display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
          <span>{t('reports.total')}</span><span>{moneyText(total.commission, currency)}</span>
        </div>
      </div>
    );
  }
  const cols = `minmax(0, 2.4fr) minmax(150px, 1.4fr) 70px minmax(110px, 1fr) minmax(110px, 1fr)${action ? ' auto' : ''}`;
  const head = { fontSize: 12, fontWeight: 600, letterSpacing: 1.1, color: L.muted, textTransform: 'uppercase' };
  const num = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  return (
    <div data-testid={testid} role="table" style={{ display: 'flex', flexDirection: 'column' }}>
      <div role="row" style={{ display: 'grid', gridTemplateColumns: cols, gap: 14, padding: '0 0 8px' }}>
        <span role="columnheader" style={head}>{t('reports.col.company')}</span>
        <span role="columnheader" style={head}>{t('reports.col.share')}</span>
        <span role="columnheader" style={head}>{t('reports.col.rate')}</span>
        <span role="columnheader" style={{ ...head, ...num }}>{netLabel}</span>
        <span role="columnheader" style={{ ...head, ...num }}>{commissionLabel}</span>
        {action && <span role="columnheader" />}
      </div>
      {shown.map((r) => (
        <div key={rowKey(r)} role="row" data-testid={r.others ? 'company-others' : `company-${r.supplier.id}`} style={{ display: 'grid', gridTemplateColumns: cols, gap: 14, alignItems: 'center', padding: '10px 0', borderTop: `1px solid ${L.rowLine}`, fontSize: 16 }}>
          <span role="cell" style={{ overflowWrap: 'anywhere' }}>{nameCell(r)}</span>
          <span role="cell"><Share share={r.share} color={barColor} /></span>
          <span role="cell"><RateCell row={r} t={t} /></span>
          <span role="cell" style={num}>{moneyText(r.net, currency)}</span>
          <span role="cell" style={{ ...num, fontWeight: 600 }}>{cm(r)}</span>
          {action && <span role="cell" style={{ textAlign: 'right' }}>{btn(r)}</span>}
        </div>
      ))}
      {toggle}
      <div role="row" data-testid="company-total" style={{ display: 'grid', gridTemplateColumns: cols, gap: 14, padding: '12px 0 0', marginTop: toggle ? 10 : 0, borderTop: `2px solid ${L.border}`, fontSize: 16, fontWeight: 700 }}>
        <span role="cell">{t('reports.total')}</span><span /><span />
        <span role="cell" style={num}>{moneyText(total.net, currency)}</span>
        <span role="cell" style={num}>{moneyText(total.commission, currency)}</span>
        {action && <span />}
      </div>
    </div>
  );
}
